import { reportError } from '@pypestream/utils';

import { ErrorTags } from './types';

/**
 * Splits a thrown message into the human part, the JSON payload the API
 * attached, and the GraphQL query inside it.
 *
 * Moved here verbatim from the two apps, which each carried an identical copy.
 */
export const formatErrorMessage = (message: string) => {
  const result: {
    msg: string | null;
    jsonMsg: string | null;
    query: string | null;
  } = {
    msg: null,
    jsonMsg: null,
    query: null,
  };

  const startIndexOfJson = message.indexOf('{');

  if (
    startIndexOfJson !== -1 &&
    message.slice(startIndexOfJson).endsWith('}')
  ) {
    result.msg = message.slice(0, startIndexOfJson);
    const jsonMessage = JSON.parse(message.slice(startIndexOfJson));
    result.query = jsonMessage.request.query;
    delete jsonMessage.request.query;
    result.jsonMsg = JSON.stringify(jsonMessage, null, 2);
  } else {
    result.msg = message;
  }

  return result;
};

/**
 * Keeps only the caller's own tags: `local` and `global` describe the boundary's
 * own state, so a caller asking for them would always match.
 */
export const filterErrorTags = (tags?: ErrorTags | ErrorTags[]) => {
  let userTags: Omit<ErrorTags, ErrorTags.local | ErrorTags.global>[] = [];

  if (tags) {
    userTags = (typeof tags === 'string' ? [tags] : tags).filter(
      (tag) => tag && tag !== ErrorTags.global && tag !== ErrorTags.local
    );
  }

  return userTags;
};

interface TaggedState {
  hasTag: (tag: string) => boolean;
}

/**
 * Reads the boundary's flags out of a set of machine states.
 *
 * Takes the states as an argument instead of calling the app's XState hook, so
 * the rule is one implementation and is testable without a running machine.
 */
export const resolveErrorFlags = (
  states: TaggedState[],
  userTags: Omit<ErrorTags, ErrorTags.local | ErrorTags.global>[] = []
) => ({
  isLocalError: states.some((state) => state.hasTag(ErrorTags.local)),
  isXstateError: states.some((state) => state.hasTag(ErrorTags.xstate)),
  isGlobalError: states.some((state) => state.hasTag(ErrorTags.global)),
  hasUserTags: !userTags.length
    ? false
    : states.some((state) =>
        userTags.every((tag) => state.hasTag(tag as string))
      ),
});

/**
 * Logs a render-phase crash and reports which React subtree it happened in.
 *
 * Manager has reported the component stack since before the two copies
 * diverged; pro-studio did not. Sharing the boundary makes it true for both.
 */
export const logError = (
  error: Error,
  info: { componentStack?: string | null }
) => {
  console.log(
    '%cERROR!>>>>>>>>>>>>>>>>>>>>>>>>>>>>>',
    'color: red; font-size: larger; font-weight: bold',
    error,
    info?.componentStack
  );

  reportError(error, {
    source: 'ErrorBoundary.onError',
    extra: { componentStack: info?.componentStack },
  });
};
